import React from 'react';

import Box from '@amzn/awsui-components-console/box';
import Icon from '@amzn/awsui-components-console/icon';
import Link, { LinkProps } from '@amzn/awsui-components-console/link';
import SpaceBetween from '@amzn/awsui-components-console/space-between';

import styles from './StackedBreadcrumbGroup.module.scss';

export interface StackedBreadcrumbItem {
  /** Primary text displayed on the top line (optional — omit for single-line items) */
  readonly topText?: string;
  /** Secondary text displayed on the bottom line (required) */
  readonly bottomText: string;
  /** URL for navigation; if omitted, the item is not clickable */
  readonly href?: string;
  /** Click handler for the item (receives the Cloudscape Link follow event) */
  readonly onClick?: (event: CustomEvent<LinkProps.FollowDetail>) => void;
}

export interface StackedBreadcrumbGroupProps {
  /** Breadcrumb items. The last item is treated as the current page (non-clickable). */
  readonly items: readonly StackedBreadcrumbItem[];
  /**
   * Called when a breadcrumb link is followed.
   * Use `event.preventDefault()` to handle navigation with react-router
   * (preserves Ctrl/Cmd+click for opening in a new tab).
   * `event.detail.href` contains the target href.
   */
  readonly onFollow?: (event: CustomEvent<LinkProps.FollowDetail>) => void;
}

const HOME_ITEM: StackedBreadcrumbItem = {
  bottomText: 'Home',
  href: '/',
};

/**
 * A breadcrumb group where each item displays two stacked lines of text.
 * Composes Cloudscape Box, Link, Icon, and SpaceBetween for minimal custom CSS.
 */
const StackedBreadcrumbGroup: React.FC<StackedBreadcrumbGroupProps> = ({
  items,
  onFollow,
}) => {
  const allItems = [HOME_ITEM, ...items];

  return (
    <nav aria-label="Breadcrumbs" className={styles.nav}>
      <div className={styles.row}>
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;
          const isHome = index === 0;
          const key = `${item.href ?? ''}-${item.bottomText}`;
          const isSingleLine = !item.topText;

          return (
            <React.Fragment key={key}>
              {index > 0 && (
                <div className={styles.separator}>
                  <Box color="text-status-inactive">
                    <Icon name="angle-right" size="small" />
                  </Box>
                </div>
              )}
              {isHome ? (
                /* Home item: single centered line, no placeholder */
                <span
                  className={styles.singleLineItem}
                  data-testid="breadcrumb-home"
                >
                  <Link
                    href={item.href ?? '/'}
                    onFollow={(event) => {
                      if (onFollow) {
                        onFollow(event);
                      }
                    }}
                    fontSize="body-m"
                  >
                    {item.bottomText}
                  </Link>
                </span>
              ) : (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={isSingleLine ? styles.singleLineItem : undefined}
                  data-testid="breadcrumb-item"
                >
                  <SpaceBetween direction="vertical" size="xxxs">
                    {item.topText ? (
                      <Box
                        variant="span"
                        color="text-body-secondary"
                        fontSize="body-s"
                      >
                        {item.topText}
                      </Box>
                    ) : (
                      <span aria-hidden="true">
                        {/* Empty top line to maintain alignment — hidden from assistive tech */}
                        &nbsp;
                      </span>
                    )}
                    {isLast ? (
                      <Box variant="span" fontWeight="bold" fontSize="body-m">
                        {item.bottomText}
                      </Box>
                    ) : (
                      <Link
                        href={item.href ?? '#'}
                        onFollow={(event) => {
                          if (item.onClick) {
                            item.onClick(event);
                          }
                          if (onFollow) {
                            onFollow(event);
                          }
                        }}
                        fontSize="body-m"
                      >
                        {item.bottomText}
                      </Link>
                    )}
                  </SpaceBetween>
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
};

export default StackedBreadcrumbGroup;
